import { Injectable, OnDestroy, inject } from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { OrderKeys } from '@config/keys/order.keys';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { DoodOnSiteLocationModel } from '@core/models/on-site-location.model';
import { DoodOrderModel } from '@store/order/order.model';

import {
  ORDER_DETAILS_BLOCK_LOADING_VM,
  OrderDetailsBlockDataSource,
  OrderDetailsBlockViewModel,
} from './order-details-block-view-model';

@Injectable()
export class OrderDetailsBlockStoreDataSource implements OrderDetailsBlockDataSource, OnDestroy {
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly onSiteLocationSelector = inject(OnSiteLocationStoreSelector);
  private readonly onSiteLocationsService = inject(OnSiteLocationsService);
  private readonly destroy$ = new Subject<void>();

  private readonly order$: Observable<DoodOrderModel | null> = this.orderSelector.selectActive.pipe(
    map(order => {
      if (!order) {
        return null;
      }
      return order[OrderKeys.MultiShop]
        ? ((order[OrderKeys.ChildOrders] as unknown as DoodOrderModel) ?? null)
        : order;
    }),
  );

  private readonly onSiteLocationName$: Observable<string> = this.orderSelector.selectActive.pipe(
    switchMap(order => {
      if (order?.onsite_location_id) {
        return this.onSiteLocationSelector.selectLocation(order.onsite_location_id);
      }
      return of(undefined);
    }),
    map((location: DoodOnSiteLocationModel | undefined) => location?.name ?? ''),
  );

  readonly vm$: Observable<OrderDetailsBlockViewModel> = combineLatest([
    this.order$,
    this.onSiteLocationName$,
  ]).pipe(
    map(([order, onSiteLocationName]) =>
      order
        ? { order, onSiteLocationName }
        : { ...ORDER_DETAILS_BLOCK_LOADING_VM, onSiteLocationName },
    ),
  );

  constructor() {
    this.loadOrderShopSlots();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOrderShopSlots(): void {
    this.orderSelector.selectActive
      .pipe(
        switchMap(order => {
          if (order?.onsite_location_id && order.shop) {
            return this.onSiteLocationsService.loadOnSiteLocations$(order.shop.id);
          }
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}
